import React, { useState, useMemo, useEffect } from 'react';
import { ArrowLeft, Search, Filter, Download, Save, Check, X, ChevronDown, ChevronUp, ArrowUp, ArrowDown, ArrowUpDown, DollarSign } from 'lucide-react';
import { formatCurrency, formatDate, getRegistrationFeeLabel } from '../../../utils/helpers';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, addDoc } from 'firebase/firestore';
import { db, auth } from '../../../services/firebase';

const RegistrationReport = ({
    players,
    expenses,
    walletTransactions,
    paymentSubmissions,
    onBack,
    clubName
}) => {
    // Filters State
    const [formatFilter, setFormatFilter] = useState('all'); // all, leather, tennis, both
    const [statusFilter, setStatusFilter] = useState('all'); // all, signed-up, awaiting, registered-pending, registered-approved
    const [waivedFilter, setWaivedFilter] = useState('all'); // all, yes, no
    const [sortConfig, setSortConfig] = useState({ key: 'name', direction: 'asc' }); // Sorting State
    const [dateRange, setDateRange] = useState({ start: '', end: '' });
    const [selectedPlayers, setSelectedPlayers] = useState([]); // Multi-select ID array
    const [playerSearchTerm, setPlayerSearchTerm] = useState(''); // Internal search for dropdown
    const [isPlayerDropdownOpen, setIsPlayerDropdownOpen] = useState(false);
    const dropdownRef = React.useRef(null);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsPlayerDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Editing State


    // Data Loading State
    const [registrationForms, setRegistrationForms] = useState({}); // { playerId: formData }
    const [loadingForms, setLoadingForms] = useState(false);

    // Load Registration Forms on mount
    useEffect(() => {
        const loadForms = async () => {
            setLoadingForms(true);
            try {
                const formsRef = collection(db, 'registrationForms');
                const snapshot = await getDocs(formsRef);
                const formsData = {};
                snapshot.forEach(doc => {
                    formsData[doc.id] = doc.data();
                });
                setRegistrationForms(formsData);
            } catch (error) {
                // Ignore permission errors (common for restricted admins)
                if (error.code !== 'permission-denied') {
                    console.error("Error loading registration forms:", error);
                } else {
                    console.warn("Notice: restricted access to registration forms. report will use player profile data.");
                }
            } finally {
                setLoadingForms(false);
            }
        };
        loadForms();
    }, []);

    // Process Data
    const reportData = useMemo(() => {
        return players.map(player => {
            const regForm = registrationForms[player.id];
            const submittedAt = regForm?.submittedAt || regForm?.createdAt;

            // Determine Formats (User choice from DB or default from previous logic)
            const formats = regForm?.formats || player.formats || { leather: false, tennis: false };

            // Calculate Applicable Initial Donation
            let calculatedFee = 0;
            if (formats.leather) {
                calculatedFee = 250; // Leather covers everything ($250)
            } else if (formats.tennis) {
                calculatedFee = 125; // Tennis only ($125)
            }

            // Sticky Fee Logic: Use stored Applicable Donation if higher (prevents fee drop on format downgrade)
            // But if format change implies a HIGHER fee, use that.
            let applicableDonation = Math.max(regForm?.applicableDonation || 0, calculatedFee);

            // Calculate Paid & Waived
            const playerTxns = walletTransactions.filter(t =>
                t.userId === player.id &&
                t.status === 'approved' &&
                (t.type === 'payment' || t.type === 'waiver')
                // Filter strictly for Initial Donation expenses? 
                // Logic: Checks all payments unless we link specific expense IDs. 
                // For simplified "Registration Report", usually we look at total balance or specific Reg Fee expenses.
                // Better approach: Look for expense ID corresponding to Initial Donations.
            );

            // Find Initial Donation expenses assigned to this player
            const initialDonationExpenses = expenses.filter(e =>
                (e.isInitialDonation || e.isRegistrationFee) &&
                e.splitAmong?.includes(player.id)
            );
            const initialDonationExpenseIds = new Set(initialDonationExpenses.map(e => e.id));

            // Relaxed Logic: Count ALL approved payments from this user towards their registration.
            // This aligns with "Account Balance" model where any payment reduces total dues.
            const relatedTxns = playerTxns;

            // Old Strict Logic (Commented out):
            // const relatedTxns = playerTxns.filter(t => initialDonationExpenseIds.has(t.expenseId));
            if (['Mayank Chawla', 'Rohit Singh'].includes(player.name)) {
                console.group(`🔍 Debug Reg Report: ${player.name}`);
                console.log('Formats:', formats);
                console.log('Calculated/Applicable Donation:', applicableDonation);
                console.log('Found Init Donation Expenses:', initialDonationExpenses);
                console.log('Expense IDs:', Array.from(initialDonationExpenseIds));
                console.log('All Player Txns:', playerTxns);
                console.log('Related (Linked) Txns:', relatedTxns);
                console.groupEnd();
            }

            const paid = Math.abs(relatedTxns.filter(t => t.type === 'payment').reduce((sum, t) => sum + t.amount, 0));
            const waived = Math.abs(relatedTxns.filter(t => t.type === 'waiver').reduce((sum, t) => sum + t.amount, 0));

            const netDues = Math.max(0, applicableDonation - paid - waived);

            // Payment Status
            // Payment Status
            let paymentStatus = 'Pending';
            if (netDues <= 0 && applicableDonation > 0) paymentStatus = 'Approved';
            else if (paid > 0) paymentStatus = 'Partial';
            if (applicableDonation === 0) paymentStatus = 'N/A'; // No dues applicable

            // Sub Status (Registration Status)
            let subStatus = 'Pending';
            if (player.registrationStatus === 'approved') subStatus = 'Approved';
            else if (player.registrationStatus === 'pending') subStatus = 'Awaited';
            else if (!player.registrationStatus) subStatus = '-';

            // Check for approved Initial Donation payment
            const hasApprovedInitialPayment = paymentSubmissions?.some(sub =>
                (sub.playerId === player.id || sub.userId === player.id) &&
                sub.status === 'approved' &&
                sub.notes &&
                sub.notes.toLowerCase().includes('initial donation')
            );

            // Unified Status Determination
            // Use registration status from player profile OR the form itself (source of truth) OR implied by approved payment
            const currentRegStatus = player.registrationStatus || regForm?.status || (hasApprovedInitialPayment ? 'approved' : null);

            let unifiedStatus = 'Signed-up'; // Default

            if (currentRegStatus === 'pending') {
                unifiedStatus = 'Awaiting Registration';
            } else if (currentRegStatus === 'approved' || currentRegStatus === 'completed') {
                if (netDues <= 0.01) {
                    unifiedStatus = 'Registered Approved';
                } else {
                    unifiedStatus = 'Registered Pending';
                }
            } else if (!currentRegStatus) {
                unifiedStatus = 'Signed-up';
            } else {
                unifiedStatus = 'Signed-up';
            }

            return {
                id: player.id,
                clubId: player.clubId,
                name: player.name,
                email: player.email,
                formats,
                applicableDonation,
                paid,
                waived,
                netDues,
                paymentStatus,
                subStatus,
                unifiedStatus,
                registrationDate: submittedAt ? new Date(submittedAt) : null,
                regFormExists: !!regForm
            };
        });
    }, [players, registrationForms, expenses, walletTransactions]);

    // Filtering Logic
    const filteredData = useMemo(() => {
        return reportData.filter(item => {
            // Name Search
            // Name Search - Removed in favor of Multi-Select
            // if (searchTerm && !item.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;

            // Selected Players (Multi-select simulation)
            if (selectedPlayers.length > 0 && !selectedPlayers.includes(item.id)) return false;

            // Format Filter
            if (formatFilter !== 'all') {
                if (formatFilter === 'leather' && !item.formats.leather) return false;
                if (formatFilter === 'tennis' && !item.formats.tennis) return false;
                if (formatFilter === 'both' && (!item.formats.leather || !item.formats.tennis)) return false;
            }

            // Unified Status Filter
            if (statusFilter !== 'all') {
                const s = item.unifiedStatus.toLowerCase().replace(/ /g, '-'); // e.g. "Registered Pending" -> "registered-pending"
                if (s !== statusFilter) return false;
            }

            // Waived Filter
            if (waivedFilter !== 'all') {
                const isWaived = item.waived > 0;
                if (waivedFilter === 'yes' && !isWaived) return false;
                if (waivedFilter === 'no' && isWaived) return false;
            }

            // Date Range Filter
            if (dateRange.start && item.registrationDate && item.registrationDate < new Date(dateRange.start)) return false;
            if (dateRange.end && item.registrationDate && item.registrationDate > new Date(dateRange.end)) return false;
            if ((dateRange.start || dateRange.end) && !item.registrationDate) return false; // Exclude empty dates if filtering by date

            return true;
            return true;
        }).sort((a, b) => {
            const { key, direction } = sortConfig;
            let comparison = 0;

            switch (key) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'applicableDonation':
                    comparison = a.applicableDonation - b.applicableDonation;
                    break;
                case 'paid':
                    comparison = a.paid - b.paid;
                    break;
                case 'waived':
                    comparison = a.waived - b.waived;
                    break;
                case 'netDues':
                    comparison = a.netDues - b.netDues;
                    break;
                case 'paymentStatus':
                    comparison = a.paymentStatus.localeCompare(b.paymentStatus);
                    break;
                case 'subStatus':
                    comparison = a.subStatus.localeCompare(b.subStatus);
                    break;
                case 'unifiedStatus':
                    comparison = a.unifiedStatus.localeCompare(b.unifiedStatus);
                    break;
                case 'registrationDate':
                    const dateA = a.registrationDate ? a.registrationDate.getTime() : 0;
                    const dateB = b.registrationDate ? b.registrationDate.getTime() : 0;
                    comparison = dateA - dateB;
                    break;
                default:
                    comparison = 0;
            }

            return direction === 'asc' ? comparison : -comparison;
        });
    }, [reportData, selectedPlayers, formatFilter, statusFilter, waivedFilter, dateRange, sortConfig]);

    // Handle Sort Helper
    const handleSort = (key) => {
        setSortConfig(current => {
            if (current.key === key) {
                return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
            }
            // Default sort direction logic per column type could go here if needed
            return { key, direction: 'asc' };
        });
    };

    // Sort Icon Helper
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="text-gray-400 ml-1 inline" />;
        return sortConfig.direction === 'asc'
            ? <ArrowUp size={14} className="text-blue-600 ml-1 inline" />
            : <ArrowDown size={14} className="text-blue-600 ml-1 inline" />;
    };

    // Handlers
    // Handlers
    const toggleFormat = async (playerId, playerName, formatType, currentFormats, clubId, playerEmail, currentApplicableDonation, paidAmount) => {
        const newStatus = !currentFormats[formatType];

        // Calculate Fee Impact
        const getFee = (f) => f.leather ? 250 : (f.tennis ? 125 : 0);

        // Refined Logic: If user has PAID $250, they are effectively at the max tier.
        // Any changes should be compared against this $250 "paid status" to prevent new charges.
        // Otherwise, proceed with the sticky fee logic using the stored applicableDonation.
        const effectiveOldFee = (paidAmount >= 250) ? 250 : (currentApplicableDonation || getFee(currentFormats));

        const newFormats = {
            ...currentFormats,
            [formatType]: newStatus
        };
        const newFee = getFee(newFormats);
        const feeDiff = newFee - effectiveOldFee;

        let feeMessage = "\nNo change to Total Due.";
        if (feeDiff > 0) feeMessage = `\nTotal Due will INCREASE by $${feeDiff}. An additional fee will be added to their balance.`;
        else if (feeDiff < 0) feeMessage = `\nTotal Due will NOT decrease. It will remain at $${effectiveOldFee.toFixed(2)} (Sticky Fee).`;

        // Confirmation Dialog
        const confirmMessage = `Are you sure you want to change the ${formatType.charAt(0).toUpperCase() + formatType.slice(1)} format for ${playerName}?\n\n` +
            `Current: ${currentFormats[formatType] ? 'Yes' : 'No'}\n` +
            `New: ${newStatus ? 'Yes' : 'No'}\n` +
            `-------------------${feeMessage}\n\n` +
            `Click OK to confirm this change.`;

        if (!window.confirm(confirmMessage)) return;

        try {
            if (!playerId) throw new Error("Player ID is missing");

            const regRef = doc(db, 'registrationForms', playerId);

            // Prepare update payload
            const updatePayload = { formats: newFormats };

            // If fee would conceptually decrease, we LOCK the applicableDonation to the OLD (higher) fee
            // If fee would conceptually decrease (or stay same for paid user), we LOCK the applicableDonation to the EFFECTIVE OLD fee
            if (feeDiff <= 0) {
                updatePayload.applicableDonation = Math.max(effectiveOldFee, newFee);
            }

            // Use setDoc with merge: true to handle both existing and new documents
            await setDoc(regRef, updatePayload, { merge: true });

            // If fee increased, add an expense
            if (feeDiff > 0) {
                await addDoc(collection(db, 'expenses'), {
                    description: `Additional Fee (${formatType} added)`,
                    amount: feeDiff,
                    clubId: clubId,
                    isRegistrationFee: true,
                    approvalStatus: 'approved',
                    splitAmong: [playerId],
                    playerEmail: playerEmail || '',
                    createdBy: auth.currentUser?.uid || 'admin',
                    createdAt: new Date().toISOString(),
                    date: new Date().toISOString(),
                    type: 'club' // Standard type
                });
            }

            // Update local state immediately for responsiveness
            // Update local state immediately for responsiveness
            setRegistrationForms(prev => ({
                ...prev,
                [playerId]: { ...prev[playerId], ...updatePayload }
            }));
        } catch (error) {
            console.error('Error updating format:', error);
            alert(`Failed to update format: ${error.message}`);
        }
    };

    const handleExport = () => {
        const csvContent = "data:text/csv;charset=utf-8,"
            + `Name,Formats,Applicable ${getRegistrationFeeLabel({ name: clubName })},Paid,Waived,Net Dues,Payment Status,Sub Status,Unified Status,Registration Date\n`
            + filteredData.map(row => {
                const formats = [];
                if (row.formats.leather) formats.push('Leather');
                if (row.formats.tennis) formats.push('Tennis');
                return `"${row.name}","${formats.join(' & ')}",${row.applicableDonation},${row.paid},${row.waived},${row.netDues},${row.paymentStatus},${row.subStatus},${row.unifiedStatus},"${row.registrationDate ? formatDate(row.registrationDate) : '-'}"`;
            }).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "registration_report.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <div className="max-w-7xl mx-auto p-6 bg-white min-h-screen">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <button onClick={onBack} className="p-2 hover:bg-gray-100 rounded-full">
                        <ArrowLeft size={24} className="text-gray-600" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Registration Report</h1>
                        <p className="text-gray-500">Manage registration formats and track payments</p>
                    </div>
                </div>
                <div className="flex gap-2">
                    <button onClick={handleExport} className="flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors">
                        <Download size={20} />
                        Export CSV
                    </button>
                </div>
            </div>

            {/* Summary Tiles */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-8">
                <div className="bg-indigo-50 px-6 py-4 border-b border-indigo-100">
                    <h3 className="font-bold text-indigo-900 flex items-center gap-2">
                        <DollarSign size={20} />
                        {getRegistrationFeeLabel({ name: clubName })} Status
                    </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 p-6">
                    {(() => {
                        const totalMembers = filteredData.length;
                        const totalDue = filteredData.reduce((sum, row) => sum + row.applicableDonation, 0);
                        const totalPaid = filteredData.reduce((sum, row) => sum + row.paid, 0);
                        const totalWaived = filteredData.reduce((sum, row) => sum + row.waived, 0);
                        const netDues = filteredData.reduce((sum, row) => sum + row.netDues, 0);

                        const collectionRate = (totalDue - totalWaived) > 0
                            ? (totalPaid / (totalDue - totalWaived)) * 100
                            : 0;

                        return (
                            <>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                    <p className="text-sm text-gray-500 font-medium">Total Members</p>
                                    <p className="text-2xl font-bold text-gray-900">{totalMembers}</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                    <p className="text-sm text-gray-500 font-medium">Total Due</p>
                                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalDue)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                    <p className="text-sm text-gray-500 font-medium">Total Paid</p>
                                    <p className="text-2xl font-bold text-green-600">{formatCurrency(totalPaid)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                    <p className="text-sm text-gray-500 font-medium">Total Waived</p>
                                    <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalWaived)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                    <p className="text-sm text-gray-500 font-medium">Net Dues</p>
                                    <p className="text-2xl font-bold text-red-600">{formatCurrency(netDues)}</p>
                                </div>
                                <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                                    <p className="text-sm text-gray-500 font-medium">Collection Rate</p>
                                    <p className="text-2xl font-bold text-blue-600">{collectionRate.toFixed(1)}%</p>
                                </div>
                            </>
                        );
                    })()}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 mb-6 space-y-4">
                <div className="flex items-center gap-2 text-gray-700 font-semibold mb-2">
                    <Filter size={20} />
                    <span>Filters</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Player Multi-Select */}
                    <div className="relative" ref={dropdownRef}>
                        <button
                            onClick={() => setIsPlayerDropdownOpen(!isPlayerDropdownOpen)}
                            className="w-full flex items-center justify-between px-3 py-2 border border-blue-200 rounded-lg hover:border-blue-300 bg-white"
                        >
                            <span className="text-gray-700 truncate text-sm">
                                {selectedPlayers.length === 0
                                    ? 'All Members'
                                    : `${selectedPlayers.length} Selected`}
                            </span>
                            <ChevronDown size={16} className="text-gray-500" />
                        </button>

                        {isPlayerDropdownOpen && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 flex flex-col">
                                <div className="p-2 border-b border-gray-100">
                                    <input
                                        type="text"
                                        placeholder="Search players..."
                                        value={playerSearchTerm}
                                        onChange={(e) => setPlayerSearchTerm(e.target.value)}
                                        className="w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:border-blue-500"
                                        autoFocus
                                    />
                                </div>
                                <div className="overflow-y-auto flex-1 p-1">
                                    {/* Actions */}
                                    <div className="flex justify-between px-2 py-1 text-xs text-blue-600 font-medium border-b border-gray-50 mb-1">
                                        <button onClick={() => setSelectedPlayers(players.map(p => p.id))}>Select All</button>
                                        <button onClick={() => setSelectedPlayers([])}>Clear</button>
                                    </div>

                                    {players
                                        .filter(p => p.name.toLowerCase().includes(playerSearchTerm.toLowerCase()))
                                        .map(player => (
                                            <label key={player.id} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedPlayers.includes(player.id)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedPlayers(prev => [...prev, player.id]);
                                                        } else {
                                                            setSelectedPlayers(prev => prev.filter(id => id !== player.id));
                                                        }
                                                    }}
                                                    className="rounded text-blue-600 focus:ring-blue-500"
                                                />
                                                <span className="truncate">{player.name}</span>
                                            </label>
                                        ))}
                                    {players.filter(p => p.name.toLowerCase().includes(playerSearchTerm.toLowerCase())).length === 0 && (
                                        <div className="p-2 text-center text-xs text-gray-500">No players found</div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Format Filter */}
                    <select
                        value={formatFilter}
                        onChange={(e) => setFormatFilter(e.target.value)}
                        className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Formats</option>
                        <option value="leather">Leather</option>
                        <option value="tennis">Tennis</option>
                        <option value="both">Both</option>
                    </select>

                    {/* Unified Status Filter */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">All Statuses</option>
                        <option value="signed-up">Signed-up</option>
                        <option value="awaiting-registration">Awaiting Registration</option>
                        <option value="registered-pending">Registered Pending</option>
                        <option value="registered-approved">Registered Approved</option>
                    </select>

                    {/* Waived Filter */}
                    <select
                        value={waivedFilter}
                        onChange={(e) => setWaivedFilter(e.target.value)}
                        className="p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                    >
                        <option value="all">Waived: All</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                    </select>

                    {/* Date Range Start */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">From:</span>
                        <input
                            type="date"
                            value={dateRange.start}
                            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                            className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>

                    {/* Date Range End */}
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">To:</span>
                        <input
                            type="date"
                            value={dateRange.end}
                            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                            className="w-full p-2 border rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>


                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto bg-white rounded-xl shadow border border-gray-200">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="bg-gray-100 border-b border-gray-200 text-gray-700 text-sm">
                            <th className="p-4 font-semibold cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('name')}>
                                <div className="flex items-center">Name <SortIcon columnKey="name" /></div>
                            </th>
                            <th className="p-4 font-semibold">Formats</th>
                            <th className="p-4 font-semibold text-right cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('applicableDonation')}>
                                <div className="flex items-center justify-end">Applicable {getRegistrationFeeLabel({ name: clubName })} <SortIcon columnKey="applicableDonation" /></div>
                            </th>
                            <th className="p-4 font-semibold text-right cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('paid')}>
                                <div className="flex items-center justify-end">Paid <SortIcon columnKey="paid" /></div>
                            </th>
                            <th className="p-4 font-semibold text-right cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('waived')}>
                                <div className="flex items-center justify-end">Waived <SortIcon columnKey="waived" /></div>
                            </th>
                            <th className="p-4 font-semibold text-right cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('netDues')}>
                                <div className="flex items-center justify-end">Net Dues <SortIcon columnKey="netDues" /></div>
                            </th>
                            <th className="p-4 font-semibold text-center cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('paymentStatus')}>
                                <div className="flex items-center justify-center">Payment Status <SortIcon columnKey="paymentStatus" /></div>
                            </th>
                            <th className="p-4 font-semibold text-center cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('subStatus')}>
                                <div className="flex items-center justify-center">Sub Status <SortIcon columnKey="subStatus" /></div>
                            </th>

                            <th className="p-4 font-semibold text-right cursor-pointer hover:bg-gray-200 transition-colors" onClick={() => handleSort('registrationDate')}>
                                <div className="flex items-center justify-end">Reg. Date <SortIcon columnKey="registrationDate" /></div>
                            </th>

                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {filteredData.length === 0 ? (
                            <tr>
                                <td colSpan="10" className="p-8 text-center text-gray-500">
                                    No records found matching filters.
                                </td>
                            </tr>
                        ) : (
                            filteredData.map(row => {
                                const currentFormats = row.formats;

                                return (
                                    <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="p-4">
                                            <div className="flex items-center gap-2">
                                                <div className="font-medium text-gray-900">{row.name}</div>
                                                <div
                                                    className={`w-3 h-3 rounded-full shadow-sm ${row.unifiedStatus === 'Registered Approved' ? 'bg-green-700' : // Dark Green
                                                        row.unifiedStatus === 'Registered Pending' ? 'bg-green-400' : // Light Green
                                                            row.unifiedStatus === 'Awaiting Registration' ? 'bg-yellow-400' : // Yellow
                                                                'bg-yellow-400' // Signed-up (Yellow)
                                                        }`}
                                                    title={row.unifiedStatus}
                                                />
                                            </div>

                                        </td>

                                        {/* Formats (Editable) */}
                                        <td className="p-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => row.regFormExists && toggleFormat(row.id, row.name, 'leather', row.formats, row.clubId, row.email, row.applicableDonation, row.paid)}
                                                    disabled={!row.regFormExists}
                                                    className={`flex items-center gap-1 px-2 py-1 rounded border text-xs select-none transition-all ${currentFormats.leather
                                                        ? 'bg-amber-100 border-amber-300 text-amber-800 font-semibold'
                                                        : 'bg-gray-50 border-gray-200 text-gray-400'
                                                        } ${!row.regFormExists ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'}`}
                                                    title={!row.regFormExists ? "User has not submitted registration form" : "Toggle Leather"}
                                                >
                                                    Leather ({currentFormats.leather ? 'Yes' : '-'})
                                                </button>

                                                <button
                                                    onClick={() => row.regFormExists && toggleFormat(row.id, row.name, 'tennis', row.formats, row.clubId, row.email, row.applicableDonation, row.paid)}
                                                    disabled={!row.regFormExists}
                                                    className={`flex items-center gap-1 px-2 py-1 rounded border text-xs select-none transition-all ${currentFormats.tennis
                                                        ? 'bg-green-100 border-green-300 text-green-800 font-semibold'
                                                        : 'bg-gray-50 border-gray-200 text-gray-400'
                                                        } ${!row.regFormExists ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-sm'}`}
                                                    title={!row.regFormExists ? "User has not submitted registration form" : "Toggle Tennis"}
                                                >
                                                    Tennis ({currentFormats.tennis ? 'Yes' : '-'})
                                                </button>
                                            </div>
                                        </td>






                                        <td className="p-4 text-right font-mono text-gray-700">
                                            {formatCurrency(row.applicableDonation)}
                                        </td>
                                        <td className="p-4 text-right font-mono text-green-700">
                                            {formatCurrency(row.paid)}
                                        </td>
                                        <td className="p-4 text-right font-mono text-purple-700">
                                            {formatCurrency(row.waived)}
                                        </td>
                                        <td className="p-4 text-right font-mono font-bold text-gray-900">
                                            {formatCurrency(row.netDues)}
                                        </td>



                                        <td className="p-4 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.paymentStatus === 'Approved' ? 'bg-green-100 text-green-700' :
                                                row.paymentStatus === 'Partial' ? 'bg-yellow-100 text-yellow-700' :
                                                    row.paymentStatus === 'Pending' ? 'bg-red-100 text-red-700' :
                                                        'bg-gray-100 text-gray-500'
                                                }`}>
                                                {row.paymentStatus}
                                            </span>
                                        </td>

                                        <td className="p-4 text-center">
                                            <span className={`px-2 py-1 rounded-full text-xs font-semibold ${row.subStatus === 'Approved' ? 'bg-green-100 text-green-700' :
                                                row.subStatus === 'Awaited' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-gray-100 text-gray-500'
                                                }`}>
                                                {row.subStatus}
                                            </span>
                                        </td>

                                        <td className="p-4 text-right text-sm text-gray-600">
                                            {row.registrationDate ? formatDate(row.registrationDate) : '-'}
                                        </td>



                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table >
            </div >
        </div >
    );
};

export default RegistrationReport;
